<template>
  <div class="mt-1">
    <!-- Gesamtbetrag Header -->
    <div class="d-flex justify-content-between align-items-center mb-4 mt-4">
      <div class="d-flex align-items-center">
        <div style="border: 1px solid #e5eaee; border-radius: 5px; padding: 2px 5px">
          <Earth class="mr-1"></Earth>
          <b>{{ reiseterminkuerzelTourId }}</b>
        </div>
      </div>
      <div class="d-flex align-items-center">
        <div class="mr-4" style="border: 1px solid #e5eaee; border-radius: 5px; padding: 2px 5px">
          <Coins class="mr-1"></Coins>
          <span style="font-weight: 700; size: 14px">Gesamtbetrag:</span>
          {{ hasMultipleWaehrungen ? '-' : `${formatBetrag(gesamtbetrag)} ${gesamtWaehrung}` }}
        </div>
        <b-button @click="toggleAllBnr" class="icon-button">
          <Maximize2 v-if="!areAllBnrOpen" :size="20" />
          <Minimize2 v-else :size="20" />
        </b-button>
      </div>
    </div>

    <!-- Vorgang Row -->
    <div v-for="gruppe in leistungenNachBnr" :key="gruppe.bnr" class="expandable-row mb-4">
      <div
        class="section-header d-flex justify-content-between align-items-center py-2 px-4"
        @click="toggleBnr(gruppe.bnr)"
        style="cursor: pointer"
      >
        <span style="font-weight: 600; font-size: 16px">BNR: {{ gruppe.bnr }}</span>
        <div class="d-flex align-items-center">
          <div class="mr-4" style="min-width: 170px; text-align: end">
            <span style="font-weight: 700; size: 14px">Betrag:</span>
            {{ hasMultipleWaehrungen ? '-' : `${formatBetrag(gruppe.betrag)} ${gruppe.waehrung}` }}
          </div>
          <ChevronDown v-if="!isBnrOpen(gruppe.bnr)" :size="20" />
          <ChevronUp v-else :size="20" />
        </div>
      </div>

      <!-- Leistungen Tabelle -->
      <b-collapse :visible="isBnrOpen(gruppe.bnr)">
        <div class="leistungen-table-wrapper">
          <b-table
            :items="gruppe.leistungen"
            :fields="angezeigteLeistungenFields"
            class="leistungen-table"
            responsive
            primary-key="id"
            :tbody-tr-class="rowClass"
          >
            <template #cell(betrag)="row">
              {{ formatBetrag(row.item.betrag) }} {{ row.item.waehrung }}
            </template>
            <template #cell(anzahlLeistungen)="row">
              <CellRendererAnzahlLeistungen
                :params="{
                  data: row.item,
                  onToggle: leistungId => toggleExpand(leistungId),
                }"
              />
            </template>
            <template #cell(status)="row">
              <template v-if="!row.item.isSubRow">
                <div
                  v-if="
                    row.item.isFreigegeben &&
                    row.item.freigabebetrag &&
                    row.item.betrag !== row.item.freigabebetrag
                  "
                  v-b-tooltip.hover.bottom
                  :title="`Freigegebener Betrag: ${formatBetrag(row.item.freigabebetrag)} ${
                    row.item.waehrung || ''
                  }`"
                  style="display: inline-block"
                >
                  <CellRendererRepruStatus :params="{ data: row.item }" />
                </div>
                <CellRendererRepruStatus v-else :params="{ data: row.item }" />
              </template>
            </template>
            <template #cell(freigabebetrag)="row">
              <template v-if="!row.item.freigabebetrag">-</template>
              <template v-else>{{ formatBetrag(row.item.freigabebetrag) }} {{ row.item.waehrung }}</template>
            </template>
            <template #cell(freigabedatum)="row">
              {{ row.item.freigabedatum ? formatDateWithoutHours(row.item.freigabedatum) : '-' }}
            </template>
            <template #cell(rechnungsdatum)="row">
              {{ row.item.rechnungsdatum ? formatDateWithoutHours(row.item.rechnungsdatum) : '-' }}
            </template>
          </b-table>
        </div>
      </b-collapse>
    </div>

    <!-- Keine Leistungen -->
    <div v-if="leistungenNachBnr.length === 0" class="text-center p-4 text-muted">
      Keine Leistungen vorhanden.
    </div>
  </div>
</template>

<script>
import { Maximize2, Minimize2, ChevronUp, ChevronDown, Coins, Earth } from 'lucide-vue';
import { mapState, mapGetters } from 'vuex';
import {
  formatDateWithoutHours,
  formatRechnungsnummern,
  parseFullUsername,
} from '@/core/common/helpers/utils.js';
import { buildDisplayedLeistungen, toggleExpandedId } from '@/core/common/helpers/teilbetraege-sub-rows.js';
import CellRendererRepruStatus from '@/components/vorgaenge/cell-renderer-repru-status.vue';
import CellRendererAnzahlLeistungen from '@/components/vorgaenge/cell-renderer-anzahl-leistungen.vue';
import apiService from '@/core/common/services/api.service';

export default {
  name: 'AufgabenLeistungen',
  components: {
    Maximize2,
    Minimize2,
    ChevronUp,
    ChevronDown,
    Coins,
    CellRendererRepruStatus,
    CellRendererAnzahlLeistungen,
    Earth,
  },
  data() {
    return {
      bnrOpenState: {},
      tourId: null,
      // IDs der aufgeklappten Main-Leistungen für die Teilbeträge-Sub-Zeilen
      expandedLeistungIds: new Set(),
      leistungenFields: [
        {
          key: 'kreditor',
          label: 'Kreditor',
          formatter: value => value ?? '-',
          thStyle: { width: '6%' },
        },
        {
          key: 'npx',
          label: 'PAX VG',
          formatter: (value, key, item) => item.vorgang?.npx ?? '-',
          thStyle: { width: '5%' },
        },
        {
          key: 'betrag',
          label: 'Betrag',
          formatter: value => value ?? '-',
          thStyle: { width: '7%' },
        },
        {
          key: 'anzahlLeistungen',
          label: 'Anzahl Leistungen',
          tdClass: 'text-center',
          thClass: 'text-center',
          thStyle: { width: '6%' },
        },
        {
          key: 'status',
          label: 'Status',
          tdClass: 'text-center',
          thClass: 'text-center',
          thStyle: { width: '5%' },
        },
        {
          key: 'teilleistungskuerzel',
          label: 'Teilleistungskürzel',
          formatter: value => (value == null || value === '' ? '-' : value),
          thStyle: { width: '10%' },
        },
        {
          key: 'leistungskuerzel',
          label: 'Leistungskürzel',
          formatter: value => value ?? '-',
          thStyle: { width: '10%' },
        },
        {
          key: 'satzart',
          label: 'Satzart',
          formatter: value => (value == null || value === '' ? '-' : value),
          thStyle: { width: '5%' },
        },
        {
          key: 'rechnungsnummer',
          label: 'Rechnungsnummer',
          formatter: (value, key, item) => formatRechnungsnummern(item.freigaben, item.waehrung),
          thStyle: { width: '12%' },
        },
        {
          key: 'freigabebetrag',
          label: 'Freigabebetrag',
          formatter: value => value ?? '-',
          thStyle: { width: '8%' },
        },
        {
          key: 'freigabedatum',
          label: 'Freigabedatum',
          formatter: value => value ?? '-',
          thStyle: { width: '8%' },
        },
        {
          key: 'rechnungsdatum',
          label: 'Rechnungsdatum',
          thStyle: { width: '8%' },
        },
        {
          key: 'freigegebenVon',
          label: 'Freigegeben durch',
          formatter: value => this.getUserNameById(value),
          thStyle: { width: '10%' },
        },
      ],
    };
  },
  computed: {
    ...mapState({
      aufgabeData: state => state.aufgaben.aufgabeDetail,
      users: state => state.users['users'],
      ...mapGetters(['currentUserId']),
    }),
    // Rechnungsdatum wird nur bei „Rechnung prüfen und buchen“ angezeigt
    angezeigteLeistungenFields() {
      const isRechnungPruefen = this.aufgabeData?.kategorie === 'RechnungPruefenUndBuchen';
      return this.leistungenFields.filter(field => field.key !== 'rechnungsdatum' || isRechnungPruefen);
    },
    reiseterminkuerzelTourId() {
      if (this.tourId) return this.tourId;
      const reiseTermine = this.aufgabeData?.reiseTermine || [];
      if (reiseTermine.length === 0) return '-';
      return (
        reiseTermine
          .map(rt => {
            const kuerzel = rt.reiseterminkuerzel || '';
            const parts = kuerzel.split('_');
            if (parts.length === 2) {
              const dateParts = parts[1].split('-');
              if (dateParts.length === 3) {
                return `${parts[0]} ${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`;
              }
            }
            return kuerzel;
          })
          .join(', ') || '-'
      );
    },
    leistungenMitGleichemKreditorUndGleicherWaehrung() {
      const leistungen = this.aufgabeData?.leistungen || [];
      if (leistungen.length === 0) return [];

      return leistungen.map(leistung => {
        if (leistung.waehrung) return leistung;

        const isStornoOrTeilstorno =
          leistung.leistungstyp === 'Storno' || leistung.leistungstyp === 'Teilstorno';
        if (!isStornoOrTeilstorno || !leistung.kreditor) return leistung;

        // Währung von einer anderen Leistung mit gleichem Kreditor übernehmen
        const leistungMitWaehrung = leistungen.find(
          andere =>
            andere.waehrung &&
            andere.kreditor === leistung.kreditor &&
            andere.leistungstyp !== 'Storno' &&
            andere.leistungstyp !== 'Teilstorno'
        );

        if (leistungMitWaehrung) {
          return { ...leistung, waehrung: leistungMitWaehrung.waehrung };
        }
        return leistung;
      });
    },

    // Leistungen direkt aus aufgabeData, gruppiert nach BNR
    leistungenNachBnr() {
      const leistungen = this.leistungenMitGleichemKreditorUndGleicherWaehrung;
      if (leistungen.length === 0) return [];

      // Nach BNR gruppieren
      const grouped = leistungen.reduce((acc, leistung) => {
        const bnr = leistung.bnr;
        if (!acc[bnr]) {
          acc[bnr] = [];
        }
        acc[bnr].push(leistung);
        return acc;
      }, {});

      // In Array umwandeln mit Betrag und Währung pro Gruppe
      return Object.entries(grouped).map(([bnr, leistungen]) => ({
        bnr: parseInt(bnr),
        leistungen: buildDisplayedLeistungen(leistungen, this.expandedLeistungIds),
        betrag: leistungen.reduce((sum, leistung) => sum + (leistung.betrag || 0), 0),
        waehrung: leistungen[0]?.waehrung || '',
      }));
    },

    gesamtbetrag() {
      const leistungen = this.leistungenMitGleichemKreditorUndGleicherWaehrung;
      return leistungen.reduce((sum, leistung) => sum + (leistung.betrag || 0), 0);
    },

    gesamtWaehrung() {
      const leistungen = this.leistungenMitGleichemKreditorUndGleicherWaehrung;
      if (leistungen.length === 0) return '';
      return leistungen[0]?.waehrung || '';
    },

    hasMultipleWaehrungen() {
      const leistungen = this.leistungenMitGleichemKreditorUndGleicherWaehrung;
      if (leistungen.length === 0) return false;
      const waehrungen = [...new Set(leistungen.map(leistung => leistung.waehrung))];
      return waehrungen.length > 1;
    },

    areAllBnrOpen() {
      if (this.leistungenNachBnr.length === 0) return false;
      return this.leistungenNachBnr.every(g => this.bnrOpenState[g.bnr] !== false);
    },
  },
  created() {
    if (!(this.aufgabeData?.reiseTermine?.length > 0)) {
      this.loadTourId();
    }
  },
  methods: {
    formatDateWithoutHours,
    getUserNameById(userId) {
      if (!userId) return '-';
      const user = this.users.find(u => u.id === userId);
      return user ? parseFullUsername(user) : '-';
    },
    formatBetrag(betrag) {
      if (betrag == null) return null;
      return betrag.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    isBnrOpen(bnr) {
      return this.bnrOpenState[bnr] !== false;
    },
    toggleBnr(bnr) {
      this.$set(this.bnrOpenState, bnr, !this.isBnrOpen(bnr));
    },
    toggleAllBnr() {
      const newState = !this.areAllBnrOpen;
      this.leistungenNachBnr.forEach(gruppe => {
        this.$set(this.bnrOpenState, gruppe.bnr, newState);
      });
    },
    toggleExpand(leistungId) {
      this.expandedLeistungIds = toggleExpandedId(this.expandedLeistungIds, leistungId);
    },
    rowClass(item) {
      return item?.isSubRow ? 'aufgaben-leistungen__sub-row' : '';
    },
    loadTourId() {
      const bnr = this.aufgabeData?.leistungen?.[0]?.bnr;
      if (!bnr) return;
      apiService
        .get(`Individualreise?$filter=vorgangId eq ${bnr}&$select=tourId&$top=1`)
        .then(res => {
          this.tourId = res.data?.result?.value?.[0]?.tourId || null;
        })
        .catch(() => {
          this.tourId = null;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/sass/components/expandable-section-styles.scss';

.leistungen-table-wrapper {
  border: 0.5px solid var(--border);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 16px;
}

.leistungen-table {
  margin-bottom: 0;

  ::v-deep thead th {
    background-color: var(--surface-sunken);
    border-bottom: 0.5px solid var(--border);
  }

  ::v-deep tbody tr {
    background-color: var(--surface);
    height: 37px;
  }

  ::v-deep tbody tr.aufgaben-leistungen__sub-row {
    animation: aufgaben-leistungen-fade-in 220ms ease-out;
    background-color: #f0f0f0 !important;
  }

  ::v-deep tbody tr.aufgaben-leistungen__sub-row td {
    background-color: #f0f0f0 !important;
  }
}

@keyframes aufgaben-leistungen-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
